import upperCase from 'lodash/upperCase'
import { useCallback } from 'react'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { euCountryCodes } from '../constants/euCountryCodes'
import { frenchTerritoriesCountryCodes } from '../constants/frenchTerritoriesCountryCodes'
import { TaxRateGroup } from '../types/taxRateGroup'

interface CountryCodesOptions {
  contactCountryCode: string
  organizationCountryCode: string
}

type GetRegionFromCountryOptions = Partial<CountryCodesOptions>

const isRegionLocal = ({ organizationCountryCode, contactCountryCode }: CountryCodesOptions) => {
  const organizationCountry = upperCase(organizationCountryCode)
  const contactCountry = upperCase(contactCountryCode)

  const areBothCountriesWithinFrenchTerritories =
    frenchTerritoriesCountryCodes.includes(organizationCountry) &&
    frenchTerritoriesCountryCodes.includes(contactCountry)

  const isLocal = organizationCountry.includes(contactCountry)

  return areBothCountriesWithinFrenchTerritories || isLocal
}

const isRegionInEu = ({ organizationCountryCode, contactCountryCode }: CountryCodesOptions) => {
  return (
    euCountryCodes.includes(upperCase(organizationCountryCode)) &&
    euCountryCodes.includes(upperCase(contactCountryCode))
  )
}

const getRegionFromContactAndOrganization = ({
  contactCountryCode,
  organizationCountryCode,
}: GetRegionFromCountryOptions): TaxRateGroup | undefined => {
  if (!organizationCountryCode || !contactCountryCode) {
    return undefined
  }

  const countryCodes = {
    contactCountryCode,
    organizationCountryCode,
  }

  if (isRegionLocal(countryCodes)) {
    return 'local'
  }

  if (isRegionInEu(countryCodes)) {
    return 'eu'
  }

  return 'world'
}

export const useGetRegionFromContactCountryCode = () => {
  const { organization } = useUserOrganization()
  const organizationCountryCode = organization?.countryId

  const getRegionFromContactCountryCode = useCallback(
    (contactCountryCode?: string) =>
      getRegionFromContactAndOrganization({ organizationCountryCode, contactCountryCode }),
    [organizationCountryCode],
  )

  return { getRegionFromContactCountryCode }
}
